/**
 * UserBiomarkers Component
 *
 * Description: A React component to display a user's biomarker data, categorized into favorite, completed, and pending biomarkers.
 * Includes UI for filtering by categories and toggling visibility for details.
 *
 * File Name: UserBiomarkers.tsx
 * Date: 24-August-2024
 */
/**
 * UserBiomarkers Component
 *
 * Description: A React component to display a user's biomarker data, categorized into favorite, completed, and pending biomarkers.
 * Includes UI for filtering by categories and toggling visibility for details.
 *
 * File Name: UserBiomarkers.tsx
 * Date: 24-August-2024
 */
import React, { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import httpRequest from "src/helpers/httpRequest";
import { IAddSingleBio, TabType } from "src/types/GlobalInterfaces";
import { customer, doctor, operator, superAdmin } from "src/lib/constans";
import SearchFilterInput from "../SearchFilterInput";
import PendingBiomarkerComponent from "src/Components/PendingBiomarkerComponent";
import CompletedBioMarkerComponents from "src/Components/Customer/CompletedBioMarkerComponents";
import { useLocation } from "react-router-dom";
import TabsButton from "src/Components/AllTabs/TabsButton";
import FilterIcon from "src/assets/images/website-images/filter-icon.png";
import {
  convertToKebabCase,
  GetBioMarkFilterIcons,
} from "src/helpers/helperFunctions";
import TabsSlideContainer from "../Widget/TabsSlideContainer";
import { useSelector } from "react-redux";
import { RootState } from "src/Store/store";

interface IBioCategory {
  name: string;
  _id: string;
  [key: string]: any;
}
interface ICustomer {
  date: string;
  gender: string;
}
export interface ICompletedBios {
  date: string;
  measurement: boolean;
  name: string;
  value: string;
  _id: string;
  isShown: boolean;
  isFavourite: boolean;
  biomarkerColour: string;
  barcategory: string;
  [key: string]: any;
}

type TabsType = "Vitals" | "Biomarkers";

const UserBiomarkers: React.FC<{
  id: string;
  isMyProfile: boolean;
  role: string;
  setActiveTab?: React.Dispatch<React.SetStateAction<TabsType>>;
  activeTab?: TabsType;
  onClose?: () => void;
  filterId?: string;
  isCustomer?: boolean;
}> = ({
  id,
  isMyProfile = true,
  role,
  setActiveTab,
  activeTab,
  onClose,
  filterId,
  isCustomer = true,
}) => {
  const {userDetails} = useSelector((state: RootState) => state.appReducer);
  // Global States
  const [activeFilterDropdown, setActiveFilterDropdown] =
    useState<boolean>(false);

  // Locationing
  const location = useLocation();
  const urlParams = new URLSearchParams(location?.search);
  // Retrieve specific query parameters
  const categoryId: string | null = urlParams.get("id");

  // *********************** CUSTOMER DATA *********************** //
  const [gender, setGender] = useState<string>("");
  const [dob, setDob] = useState<string>("");
  // *********************** CUSTOMER DATA *********************** //

  // *********************** CATEGORY STATES HOLD *********************** //
  const [categoryLoader, setCategoryLoader] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>(
    filterId ? filterId : categoryId ? categoryId : ""
  );
  const [biomarkerName, setBiomarkerName] = useState<string>("");
  const [allCategory, setAllCategory] = useState<IBioCategory[]>([]);
  const changeCategories = (txt: string) => {
    setSelectedCategory(txt);
    setBiomarkerName("");
  };
  // *********************** CATEGORY STATES HOLD *********************** //

  // *********************** FAVORITE BIOMARKERS STATES HOLD *********************** //
  const [favouriteLoader, setFavouriteLoader] = useState<boolean>(false);
  const [favouriteList, setFavouriteList] = useState<ICompletedBios[]>([]);
  // *********************** FAVORITE BIOMARKERS STATES HOLD *********************** //

  // *********************** COMPLETED BIOMARKERS STATES HOLD *********************** //
  const [completedLoader, setCompletedLoader] = useState<boolean>(false);
  const [completedList, setCompletedList] = useState<ICompletedBios[]>([]);
  const updateBiosHistory = (categ: string) => {
    if (categ === "favorite") {
      fetchFavoriteBios();
    } else {
      fetchCompletedBios();
    }
  };
  // *********************** COMPLETED BIOMARKERS STATES HOLD *********************** //

  // *********************** PENDING BIOMARKERS STATES HOLD *********************** //
  const [pendingLoader, setPendingLoader] = useState<boolean>(false);
  const [pendingList, setPendingList] = useState<IAddSingleBio[]>([]);

  /**
   * Handles moving a vitals item from the "to be completed" list to the "completed" list.
   * Updates the state and triggers a refetch of completed vitals.
   */
  const addToCompleted = (id: string) => {
    setPendingList((prev) => prev?.filter((v) => v?._id !== id)); //remove from toBeCompletedList;
    fetchCompletedBios(); // completed List api again cal;
  };
  // *********************** PENDING BIOMARKERS STATES HOLD *********************** //

  //API_CALL: to fecth all biomarkers categories
  const fetchBioCategory = async () => {
    setCategoryLoader(true);
    const { res, err } = await httpRequest<IBioCategory[]>({
      path: "/biomarker/getallbiomarkerCategory",
    });
    if (res) {
      setAllCategory(res);
    } else {
      toast(err, { type: "error" });
    }
    setCategoryLoader(false);
  };

  //API_CALL: to fetch favorite biomarkers based on user ID
  const fetchFavoriteBios = async () => {
    setFavouriteLoader(true);
    const { res, err } = await httpRequest<ICompletedBios[]>({
      path: `/healthprofile/biomarkers/favorites`,
    });
    if (res) {
      setFavouriteList(res || []);
    } else {
      toast(err, { type: "error" });
    }
    setFavouriteLoader(false);
  };

  //API_CALL: to fetch completed biomarkers based on user ID
  const fetchCompletedBios = async () => {
    setCompletedLoader(true);
    const { res, err } = await httpRequest<ICompletedBios[]>({
      path: `/healthprofile/biomarker/details/${id}/completed?categoryId=${selectedCategory}&search=${encodeURIComponent(biomarkerName)}`,
    });
    if (res) {
      setCompletedList(res || []);
    } else {
      toast(err, { type: "error" });
    }
    setCompletedLoader(false);
  };

  //API_CALL: to fetch pending biomarkers based on user ID
  const fetchPendingBios = async () => {
    setPendingLoader(true);
    const { res, err } = await httpRequest<{
      message: string;
      data: { biomarkerData: IAddSingleBio[]; customerData: ICustomer };
    }>({
      path: `/healthprofile/biomarker/details/${id}?categoryId=${selectedCategory}&search=${encodeURIComponent(biomarkerName)}`,
    });
    if (res) {
      setPendingList(res?.data?.biomarkerData || []);
      setGender(res?.data?.customerData?.gender || userDetails?.gender || "");
      setDob(res?.data?.customerData?.date  || userDetails?.date || "");
    } else {
      toast(err, { type: "error" });
    }
    setPendingLoader(false);
  };

  //API_CALL: to add or remove a biomarker from the favorite list
  const addAndRemoveFavourite = async (id: string) => {
    const { res, err } = await httpRequest<{ message: string }>({
      method: "put",
      path: `/healthprofile/biomarker/${id}`,
      params: {},
    });
    if (res) {
      toast(res.message, { type: "success" });
      setCompletedList((prev) =>
        prev.map((v) =>
          v?._id === id ? { ...v, isFavourite: !v?.isFavourite } : v
        )
      );
      const itemToToggle = completedList.find((v) => v?._id === id);
      if (itemToToggle) {
        const isExist = favouriteList.some((v) => v._id === id);
        if (isExist) {
          setFavouriteList((prev) => prev.filter((v) => v?._id !== id));
        } else {
          setFavouriteList((prev) => [
            ...prev,
            { ...itemToToggle, isFavourite: true },
          ]);
        }
      }
    } else {
      toast(err, { type: "error" });
    }
  };

  // Fetch biomarker categories and favorites when the component mounts
  useEffect(() => {
    fetchBioCategory();
    if (isMyProfile) {
      fetchFavoriteBios();
    }
  }, []);

  // Fetch pending and completed biomarkers when the component mounts or `id` changes
  useEffect(() => {
    if (id || filterId) {
      fetchCompletedBios();
      fetchPendingBios();
    }
  }, [id, selectedCategory, biomarkerName, filterId]);

  let tabs: TabType[] = [
    {
      name: "Vitals",
      count: 0,
    },
    {
      name: "Biomarkers",
      count: 0,
    },
  ];

  return (
    <>
      <div className="w-full flex items-center justify-between gap-1 2xl:mb-2 mb-1.5">
        {role === customer ? (
          <div className="w-fit flex flex-1">
            <TabsSlideContainer
              tabs={tabs}
              activeTab={tabs.find((tab) => tab.name === activeTab) as TabType}
              onChange={(tab: TabType) => {
                setActiveTab?.(tab.name as TabsType);
              }}
            />
          </div>
        ) : null}
        {role === customer ? (
          <div className="relative">
            <div
              className={`2xl:w-[30px] 2xl:h-[30px] w-[25px] h-[25px] relative 2xl:rounded-md rounded ${
                activeFilterDropdown
                  ? "border-gray-400 bg-gray-200"
                  : "hover:bg-gray-200 hover:border-gray-400 border-transparent"
              } border cursor-pointer transition-all flex items-center justify-center`}
              onClick={(e) => {
                e.stopPropagation();
                setActiveFilterDropdown(!activeFilterDropdown);
              }}
            >
              <img src={FilterIcon} alt="Filter Icon" className="w-[60%]" />
            </div>
            <div
              className={`w-fit 2xl:min-w-[220px] min-w-[180px] overflow-hidden bg-white absolute right-0 2xl:mt-2 mt-1 top-full shadow-all rounded origin-top-right transition-all ${
                activeFilterDropdown
                  ? "scale-100 opacity-100"
                  : "scale-0 opacity-0"
              }`}
            >
              <button
                className={`font-size-14px w-full font-Poppins-Regular text-[#2D2D2D] 2xl:px-3 px-2  ${
                  selectedCategory === "" ? "bg-gray-100" : "hover:bg-gray-50"
                } flex items-center gap-3`}
                onClick={() => {
                  setActiveFilterDropdown(false);
                  changeCategories("");
                }}
                id="health-profile-tab-overall"
              >
                <div className="border-b border-gray-300 flex items-center gap-3 py-2 w-full">
                  <img
                    src={GetBioMarkFilterIcons("") || ""}
                    alt={"Overall"}
                    className="w-[12px] 2xl:w-[15px]"
                  />
                  <p className="w-full whitespace-nowrap text-left">Overall</p>
                </div>
              </button>
              {categoryLoader ? (
                <>
                  <CircularProgress
                    size={14}
                    className="loader__extra__class ms-5"
                  />
                </>
              ) : (
                allCategory.map((v: IBioCategory) => (
                  <button
                    key={v._id}
                    className={`font-size-14px w-full font-Poppins-Regular text-[#2D2D2D] 2xl:px-3 px-2  ${
                      selectedCategory === v._id
                        ? "bg-gray-100"
                        : "hover:bg-gray-50"
                    } flex items-center gap-3`}
                    onClick={() => {
                      setActiveFilterDropdown(false);
                      changeCategories(v._id);
                    }}
                    id={`health-profile-tab-${convertToKebabCase(
                      v?.name || ""
                    )}`}
                  >
                    <div className="border-b border-gray-300 flex items-center gap-3 py-2 w-full">
                      <img
                        src={GetBioMarkFilterIcons(v.name) || ""}
                        alt={v.name}
                        className="w-[12px] 2xl:w-[15px]"
                      />
                      <p className="w-full whitespace-nowrap text-left">
                        {v.name}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        ) : null}
        {role === superAdmin || role === doctor || role === operator ? (
          <>
            <div
              className="w-full flex items-center flex-wrap gap-3 mb-3 pb-3"
              style={{ borderBottom: "2px solid #D4D4D4" }}
            >
              <button
                className={`font-size-15px w-fit py-2 font-Poppins-Regular text-[#2D2D2D] px-3  ${
                  selectedCategory === ""
                    ? "border-[#004641] bg-[#004641] text-white"
                    : "text-[#004641] border border-[#004641]"
                } flex items-center gap-3 rounded`}
                onClick={() => {
                  setActiveFilterDropdown(false);
                  changeCategories("");
                }}
                id="health-profile-tab-overall"
              >
                <p className="whitespace-nowrap text-left">Overall</p>
              </button>
              {categoryLoader ? (
                <>
                  <CircularProgress
                    size={14}
                    className="loader__extra__class ms-5"
                  />
                </>
              ) : (
                allCategory.map((v: IBioCategory) => (
                  <button
                    key={v._id}
                    className={`font-size-14px w-fit py-2 font-Poppins-Regular  px-3 ${
                      selectedCategory === v._id
                        ? "border-[#004641] bg-[#004641] text-white"
                        : "text-[#2D2D2D] border border-[#004641]"
                    } flex items-center gap-3 rounded`}
                    onClick={() => {
                      setActiveFilterDropdown(false);
                      changeCategories(v._id);
                    }}
                    id={`health-profile-tab-${convertToKebabCase(
                      v?.name || ""
                    )}`}
                  >
                    <p className="w-full whitespace-nowrap text-left">
                      {v?.name?.split(" ")[0] || ""}
                    </p>
                  </button>
                ))
              )}
            </div>
          </>
        ) : null}
      </div>
      <div className={`w-full ${role === superAdmin ? "max-w-[880px] w-[60%] min-w-[500px]" : ""}`}>
        {isMyProfile && selectedCategory === "" && (
          <>
            <div className="health-biomarkers-tab-1">
              <div>
                <h2 className="font-Poppins-Regular text-[#606060] font-size-16px">
                  <span className="theme-color-green-dark font-Poppins-Medium font-size-18px">
                    Favorite Biomarkers
                  </span>{" "}
                  (Select up to 5 favorite biomarkers.)
                </h2>
                {favouriteLoader && (
                  <div className="text-center mt-3">
                    <CircularProgress size={24} style={{ color: "#00443f" }} />
                  </div>
                )}
                <div className="health-vitals-row-main">
                  {favouriteList.length > 0 ? (
                    favouriteList.map((v: ICompletedBios, i: number) => (
                      <CompletedBioMarkerComponents
                        key={i}
                        item={v}
                        userRole="customer"
                        dob={dob}
                        gender={gender}
                        uid={id}
                        isMyProfile={true}
                        onSuccess={() => updateBiosHistory("favorite")}
                        favApiCall={() => addAndRemoveFavourite(v?._id)}
                        onClose={onClose}
                        index={i + 4000}
                        isFavId={"favorite-"}
                        isCustomer={isCustomer}
                      />
                    ))
                  ) : (
                    <p className="font-Poppins-Regular font-size-15px text-center">
                      No Items
                    </p>
                  )}
                </div>
              </div>
            </div>
            <hr className="products-tabs-hr-allOrder" />
          </>
        )}
        {/* completed list or tested biomarkers */}
        <div className="health-biomarkers-tab-1">
          <div>
            <h2 className="font-Poppins-Regular text-[#606060] font-size-16px">
              <span className="theme-color-green-dark font-Poppins-Medium font-size-18px">
                Tested Biomarkers
              </span>{" "}
              {/* ({completedList.length || 0} Biomarkers Out of{" "}
              {completedList.length + pendingList.length} Has Been Tested) */}
              ({completedList.length || 0} Biomarkers out of{" "}
              {completedList.length + pendingList.length} has been tested)
            </h2>
            {completedLoader && (
              <div className="text-center mt-3">
                <CircularProgress size={24} style={{ color: "#00443f" }} />
              </div>
            )}
            <div className="health-vitals-row-main">
              {completedList.length > 0 ? (
                completedList.map((v: ICompletedBios, i: number) => (
                  <CompletedBioMarkerComponents
                    key={i}
                    item={v}
                    userRole={role === superAdmin ? "admin" : "customer"}
                    dob={dob}
                    gender={gender}
                    uid={id}
                    isMyProfile={true}
                    onSuccess={() => updateBiosHistory("completed")}
                    favApiCall={() => addAndRemoveFavourite(v?._id)}
                    onClose={onClose}
                    index={i}
                    isCustomer={isCustomer}
                  />
                ))
              ) : (
                <p className="font-Poppins-Regular font-size-15px text-center">
                  No Items
                </p>
              )}
            </div>
          </div>
        </div>
        <hr className="products-tabs-hr-allOrder" />
        {/* to be completed or pending */}
        <div className="health-biomarkers-tab-1">
          <div>
            <div className="flex items-center justify-between">
              <h2 className="font-Poppins-Regular text-[#313131] font-size-18px">
                <span className="theme-color-green font-Poppins-SemiBold">
                  To be Completed
                </span>
              </h2>
              {role === superAdmin && (
                <div className="flex">
                  <SearchFilterInput  
                    key={selectedCategory}
                    searchVal={biomarkerName}
                    onChangeText={(val: string) => setBiomarkerName(val)}
                  />
                </div>
              )}
            </div>
            {pendingLoader && (
              <div className="text-center mt-3">
                <CircularProgress size={24} style={{ color: "#00443f" }} />
              </div>
            )}
            <div className="health-vitals-row-main">
              {pendingList.length > 0 ? (
                pendingList.map((v, index) => (
                  <PendingBiomarkerComponent
                    key={index}
                    item={v}
                    onSuccess={addToCompleted}
                    dob={dob}
                    gender={gender}
                    uid={id}
                    onClose={onClose}
                    favApiCall={() => addAndRemoveFavourite(v?._id)}
                    isMyProfile={isMyProfile}
                    index={index + 5000}
                    isCustomer={isCustomer}
                  />
                ))
              ) : (
                <p className="font-Poppins-Regular font-size-15px text-center">
                  No Items
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserBiomarkers;
